/**
 * CommandPalette - Quick action launcher (Cmd+K)
 * USER-CON-001 UI-005: Fuzzy search command palette for power users
 */
import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import {
  Search, Plus, Upload, Download, Grid, List, Map as MapIcon,
  Box, Tag, Star, DollarSign, Filter, Settings, Trash2,
  BookOpen, Package, FolderOpen, FileText, Command, X, Clock,
  ScanLine
} from 'lucide-react';

// Command definitions
const COMMANDS = [
  // Navigation
  { id: 'view-grid', label: 'Switch to Grid View', shortcut: '1', icon: Grid, category: 'View', action: 'view:grid' },
  { id: 'view-list', label: 'Switch to List View', shortcut: '2', icon: List, category: 'View', action: 'view:list' },
  { id: 'view-run', label: 'Switch to Run View', shortcut: '3', icon: MapIcon, category: 'View', action: 'view:run' },
  { id: 'view-matrix', label: 'Switch to Variant Matrix', shortcut: '4', icon: Grid, category: 'View', action: 'view:matrix' },

  // Actions
  { id: 'add-item', label: 'Add New Item', shortcut: 'N', icon: Plus, category: 'Actions', action: 'modal:add-item' },
  { id: 'import', label: 'Import Collection (CLZ/CSV)', shortcut: 'I', icon: Upload, category: 'Actions', action: 'modal:import' },
  { id: 'import-history', label: 'Import History', icon: Clock, category: 'Actions', action: 'modal:import-history' },
  { id: 'export-csv', label: 'Export to CSV', icon: Download, category: 'Actions', action: 'export:csv' },
  { id: 'export-clz', label: 'Export CLZ Format', icon: Download, category: 'Actions', action: 'export:clz' },
  { id: 'export-ebay', label: 'Export for eBay', icon: Download, category: 'Actions', action: 'export:ebay' },
  { id: 'export-filter', label: 'Export with Filters...', icon: Filter, category: 'Actions', action: 'modal:export-filter' },
  // V-ADD: scan and bulk are TABS of the one add surface, so these are add-item with a tab hint.
  // 'nav:scan-barcode' used to set window.location.href — a full reload out of the Vault to a
  // route that never read the ?tab param.
  { id: 'scan-barcode', label: 'Scan Barcode', icon: ScanLine, category: 'Actions', action: 'modal:add-item:scan' },
  { id: 'bulk-barcode', label: 'Bulk Barcode Import', icon: Upload, category: 'Actions', action: 'modal:add-item:bulk' },

  // Smart Sets
  { id: 'create-smart-set', label: 'Create Smart Set', icon: Filter, category: 'Smart Sets', action: 'modal:create-smart-set' },
  { id: 'show-keys', label: 'Show Key Issues', icon: Star, category: 'Smart Sets', action: 'filter:keys' },
  { id: 'show-graded', label: 'Show Graded Comics', icon: Tag, category: 'Smart Sets', action: 'filter:graded' },
  { id: 'show-for-sale', label: 'Show For Sale Items', icon: DollarSign, category: 'Smart Sets', action: 'filter:for-sale' },
  { id: 'show-unread', label: 'Show Unread Comics', icon: BookOpen, category: 'Smart Sets', action: 'filter:unread' },

  // Boxes
  { id: 'create-box', label: 'Create New Box', icon: Box, category: 'Organization', action: 'modal:create-box' },
  { id: 'manage-boxes', label: 'Manage Boxes', icon: FolderOpen, category: 'Organization', action: 'nav:boxes' },

  // Selected Item Actions
  { id: 'edit-item', label: 'Edit Selected Item', shortcut: 'E', icon: FileText, category: 'Selection', action: 'selection:edit', requiresSelection: true },
  { id: 'mark-for-sale', label: 'Add to Sell Queue', shortcut: 'S', icon: DollarSign, category: 'Selection', action: 'selection:sell', requiresSelection: true },
  { id: 'assign-box', label: 'Assign to Box', shortcut: 'B', icon: Box, category: 'Selection', action: 'selection:box', requiresSelection: true },
  { id: 'mark-wanted', label: 'Toggle Wanted Status', shortcut: 'G', icon: Star, category: 'Selection', action: 'selection:wanted', requiresSelection: true },
  { id: 'delete-item', label: 'Delete Selected', icon: Trash2, category: 'Selection', action: 'selection:delete', requiresSelection: true, danger: true },
];

// Fuzzy search scoring
function fuzzyMatch(query, text) {
  query = query.toLowerCase();
  text = text.toLowerCase();

  if (text.includes(query)) {
    return 100 - text.indexOf(query);
  }

  let score = 0;
  let queryIdx = 0;
  let prevMatch = -1;

  for (let i = 0; i < text.length && queryIdx < query.length; i++) {
    if (text[i] === query[queryIdx]) {
      score += prevMatch === i - 1 ? 2 : 1;
      prevMatch = i;
      queryIdx++;
    }
  }

  return queryIdx === query.length ? score : 0;
}

export default function CommandPalette({
  isOpen,
  onClose,
  onAction,
  hasSelection = false,
  selectionCount = 0,
}) {
  const [query, setQuery] = useState('');
  const [selectedIndex, setSelectedIndex] = useState(0);
  const inputRef = useRef(null);
  const listRef = useRef(null);

  // Filter and sort commands
  const filteredCommands = useMemo(() => {
    let commands = COMMANDS.filter(cmd => {
      if (cmd.requiresSelection && !hasSelection) return false;
      return true;
    });

    if (!query.trim()) {
      return commands;
    }

    return commands
      .map(cmd => ({
        ...cmd,
        score: fuzzyMatch(query, cmd.label) + fuzzyMatch(query, cmd.category),
      }))
      .filter(cmd => cmd.score > 0)
      .sort((a, b) => b.score - a.score);
  }, [query, hasSelection]);

  // Group commands by category
  const groupedCommands = useMemo(() => {
    const groups = new Map();
    filteredCommands.forEach(cmd => {
      if (!groups.has(cmd.category)) {
        groups.set(cmd.category, []);
      }
      groups.get(cmd.category).push(cmd);
    });
    return groups;
  }, [filteredCommands]);

  // Reset state when opened
  useEffect(() => {
    if (isOpen) {
      setQuery('');
      setSelectedIndex(0);
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  }, [isOpen]);

  // Keep selected item in view
  useEffect(() => {
    const list = listRef.current;
    if (!list) return;

    const selectedEl = list.querySelector(`[data-index="${selectedIndex}"]`);
    if (selectedEl) {
      selectedEl.scrollIntoView({ block: 'nearest' });
    }
  }, [selectedIndex]);

  // Keyboard navigation
  const handleKeyDown = useCallback((e) => {
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedIndex(i => Math.min(i + 1, filteredCommands.length - 1));
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSelectedIndex(i => Math.max(i - 1, 0));
        break;
      case 'Enter':
        e.preventDefault();
        if (filteredCommands[selectedIndex]) {
          onAction(filteredCommands[selectedIndex].action);
          onClose();
        }
        break;
      case 'Escape':
        e.preventDefault();
        onClose();
        break;
    }
  }, [filteredCommands, selectedIndex, onAction, onClose]);

  // Click handler
  const handleCommandClick = (command) => {
    onAction(command.action);
    onClose();
  };

  if (!isOpen) return null;

  // Flatten for index tracking
  let flatIndex = 0;

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center pt-[15vh]">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Palette */}
      <div className="relative w-full max-w-lg bg-zinc-900 border border-zinc-700 rounded-xl shadow-2xl overflow-hidden">
        {/* Search input */}
        <div className="flex items-center gap-3 p-4 border-b border-zinc-800">
          <Search className="w-5 h-5 text-zinc-500" />
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={(e) => {
              setQuery(e.target.value);
              setSelectedIndex(0);
            }}
            onKeyDown={handleKeyDown}
            placeholder="Type a command..."
            className="flex-1 bg-transparent text-white placeholder-zinc-500 outline-none text-lg"
            autoComplete="off"
            autoCorrect="off"
            spellCheck="false"
          />
          <div className="flex items-center gap-1 text-xs text-zinc-500">
            <kbd className="px-1.5 py-0.5 bg-zinc-800 rounded">esc</kbd>
            <span>to close</span>
          </div>
        </div>

        {/* Command list */}
        <div ref={listRef} className="max-h-80 overflow-y-auto">
          {filteredCommands.length === 0 ? (
            <div className="p-4 text-center text-zinc-500">
              No commands match "{query}"
            </div>
          ) : (
            Array.from(groupedCommands.entries()).map(([category, commands]) => (
              <div key={category}>
                {/* Category header */}
                <div className="px-4 py-2 text-xs font-medium text-zinc-500 uppercase tracking-wider bg-zinc-800/50">
                  {category}
                  {category === 'Selection' && selectionCount > 0 && (
                    <span className="ml-2 text-orange-400">({selectionCount} items)</span>
                  )}
                </div>

                {/* Commands */}
                {commands.map((cmd) => {
                  const idx = flatIndex++;
                  const Icon = cmd.icon;
                  const isSelected = idx === selectedIndex;

                  return (
                    <button
                      key={cmd.id}
                      data-index={idx}
                      onClick={() => handleCommandClick(cmd)}
                      className={`w-full flex items-center gap-3 px-4 py-2.5 transition-colors ${
                        isSelected
                          ? 'bg-orange-500/20 text-white'
                          : 'text-zinc-300 hover:bg-zinc-800'
                      } ${cmd.danger ? 'text-red-400' : ''}`}
                    >
                      <Icon className={`w-4 h-4 ${isSelected ? 'text-orange-400' : 'text-zinc-500'}`} />
                      <span className="flex-1 text-left">{cmd.label}</span>
                      {cmd.shortcut && (
                        <kbd className="px-1.5 py-0.5 bg-zinc-800 rounded text-xs text-zinc-400">
                          {cmd.shortcut}
                        </kbd>
                      )}
                    </button>
                  );
                })}
              </div>
            ))
          )}
        </div>

        {/* Footer */}
        <div className="p-3 border-t border-zinc-800 flex items-center justify-between text-xs text-zinc-500">
          <div className="flex items-center gap-2">
            <Command className="w-3 h-3" />
            <span>K to open</span>
          </div>
          <div className="flex items-center gap-3">
            <span><kbd className="px-1 bg-zinc-800 rounded">↑↓</kbd> navigate</span>
            <span><kbd className="px-1 bg-zinc-800 rounded">↵</kbd> select</span>
          </div>
        </div>
      </div>
    </div>
  );
}
